/**
 * ModelLibrary — the single loader/cache/clone layer for every GLB asset.
 *
 * Responsibilities (asset-pipeline spec):
 *  - load once and cache the parsed `GLTF` (one network fetch per model),
 *  - clone safely: `SkeletonUtils.clone` for skinned models so each instance
 *    owns its bones, `Object3D.clone(true)` for static ones so geometry and
 *    materials stay shared,
 *  - never throw: missing assets and load failures resolve to `null` so callers
 *    keep their procedural fallbacks,
 *  - expose instancing/LOD helpers for small repeated props.
 *
 * The module wires the self-contained Meshopt decoder once, so GLBs compressed
 * with `EXT_meshopt_compression` (gltfpack) decode without any extra decoder
 * files being copied into the build.
 */
import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { disposeObject3D } from '../utils/dispose';
import { getModelEntry, resolveModelName } from './modelManifest';

/** A fresh, consumer-owned clone of a loaded model. */
export interface ModelHandle {
  /** Deep-ish clone, safe to mutate; skinned meshes own their own skeleton. */
  scene: THREE.Group;
  /** Shared animation clips from the source GLB (drive via one mixer per clone). */
  animations: THREE.AnimationClip[];
}

/** A single load failure recorded by the library. */
export interface LoadFailure {
  name: string;
  reason: string;
}

/** Summary of what has loaded and what failed. */
export interface LoadReport {
  loaded: string[];
  failed: LoadFailure[];
}

/** Constructor options. */
export interface ModelLibraryOptions {
  /** Base URL for model files; defaults to `/models/`. */
  baseUrl?: string;
  /** Set false to skip wiring the Meshopt decoder; defaults to true. */
  meshopt?: boolean;
}

/** One placement for the `instance` helper. */
export interface InstanceTransform {
  position: THREE.Vector3;
  quaternion?: THREE.Quaternion;
  /** Uniform scale, or per-axis scale. */
  scale?: number | THREE.Vector3;
}

/** Shadow flags for the `instance` helper. */
export interface InstanceOptions {
  /** Set `InstancedMesh.castShadow`; defaults to false. */
  castShadow?: boolean;
  /** Set `InstancedMesh.receiveShadow`; defaults to false. */
  receiveShadow?: boolean;
}

const DEFAULT_BASE_URL = '/models/';
/** LOD distances used when a caller does not supply their own. */
const DEFAULT_LOD_DISTANCES: readonly number[] = [0, 60, 200];

function normalizeBaseUrl(url: string): string {
  return url.endsWith('/') ? url : `${url}/`;
}

function firstMaterial(mesh: THREE.Mesh): THREE.Material | null {
  if (Array.isArray(mesh.material)) return mesh.material[0] ?? null;
  return mesh.material ?? null;
}

export class ModelLibrary {
  private readonly loader = new GLTFLoader();
  private readonly baseUrl: string;
  /** name -> in-flight/settled load promise (null on failure). */
  private readonly cache = new Map<string, Promise<GLTF | null>>();
  /** name -> successfully parsed GLTF (synchronous accessor for `create`). */
  private readonly loaded = new Map<string, GLTF>();
  /** name -> last failure reason. */
  private readonly failures = new Map<string, string>();

  constructor(options: ModelLibraryOptions = {}) {
    this.baseUrl = normalizeBaseUrl(options.baseUrl ?? DEFAULT_BASE_URL);
    if (options.meshopt !== false) {
      // Self-contained WASM (embedded base64), no decoder path to self-host.
      this.loader.setMeshoptDecoder(MeshoptDecoder);
    }
  }

  /**
   * Load (or await in-flight loads of) the named models. Unknown names and
   * failed fetches are reported in `failed` — this method never throws.
   */
  async load(names: string[]): Promise<LoadReport> {
    const loaded: string[] = [];
    const failed: LoadFailure[] = [];

    // Expand each requested name into its full LOD chain (level 0 + `lod[]`)
    // so a single `load(['hero-hull'])` call is enough for `buildLod` to build
    // the whole chain without the caller enumerating every level.
    const toFetch: string[] = [];
    for (const rawName of [...new Set(names)]) {
      const key = resolveModelName(rawName);
      if (!key) {
        failed.push({ name: rawName, reason: 'unknown model name' });
        continue;
      }
      toFetch.push(...this.lodChain(key));
    }

    await Promise.all(
      [...new Set(toFetch)].map(async (key) => {
        const gltf = await this.fetch(key);
        if (gltf) {
          loaded.push(key);
        } else {
          failed.push({ name: key, reason: this.failures.get(key) ?? 'failed to load' });
        }
      }),
    );

    return { loaded: [...new Set(loaded)], failed };
  }

  /**
   * Synchronously return a fresh clone of an already-loaded model, or `null`
   * when the model is unknown or has not loaded yet. Callers fall back to
   * procedural geometry on `null`.
   */
  create(name: string): ModelHandle | null {
    const key = resolveModelName(name);
    if (!key) return null;
    const gltf = this.loaded.get(key);
    if (!gltf) return null;

    const entry = getModelEntry(key);
    try {
      const scene = this.cloneScene(gltf, entry?.skinned === true);
      return { scene, animations: gltf.animations };
    } catch (error) {
      console.warn(`[models] failed to clone "${key}":`, error);
      return null;
    }
  }

  /** True when the model (or an alias for it) has already loaded. */
  has(name: string): boolean {
    const key = resolveModelName(name);
    return key !== null && this.loaded.has(key);
  }

  /** Cumulative summary of everything loaded/failed so far. */
  report(): LoadReport {
    return {
      loaded: [...this.loaded.keys()],
      failed: [...this.failures].map(([name, reason]) => ({ name, reason })),
    };
  }

  /** Dispose cached GLB geometry/materials/textures and clear state. */
  dispose(): void {
    for (const gltf of this.loaded.values()) {
      disposeObject3D(gltf.scene);
    }
    this.loaded.clear();
    this.cache.clear();
    this.failures.clear();
  }

  /**
   * Extract a single instancable `{ geometry, material }` from a loaded model.
   * Multi-mesh props are merged into one geometry (their world transforms are
   * baked in), so `instance` needs exactly one draw call.
   */
  extractGeometry(name: string): { geometry: THREE.BufferGeometry; material: THREE.Material } | null {
    const key = resolveModelName(name);
    if (!key) return null;
    const gltf = this.loaded.get(key);
    if (!gltf) return null;
    return this.extractInstancedSource(gltf);
  }

  /**
   * Build an `InstancedMesh` for many copies of a small single-material prop.
   * Returns `null` (and the caller falls back to per-prop clones/procedural
   * meshes) when the model is missing, not loaded, or not instancable.
   */
  instance(
    name: string,
    transforms: InstanceTransform[],
    parent?: THREE.Object3D,
    options: InstanceOptions = {},
  ): THREE.InstancedMesh | null {
    if (transforms.length === 0) return null;
    const source = this.extractGeometry(name);
    if (!source) return null;

    const mesh = new THREE.InstancedMesh(source.geometry, source.material, transforms.length);
    mesh.castShadow = options.castShadow ?? false;
    mesh.receiveShadow = options.receiveShadow ?? false;
    const matrix = new THREE.Matrix4();
    const position = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3(1, 1, 1);

    transforms.forEach((transform, index) => {
      position.copy(transform.position);
      if (transform.quaternion) {
        quaternion.copy(transform.quaternion);
      } else {
        quaternion.identity();
      }
      if (typeof transform.scale === 'number') {
        scale.setScalar(transform.scale);
      } else if (transform.scale) {
        scale.copy(transform.scale);
      } else {
        scale.set(1, 1, 1);
      }
      matrix.compose(position, quaternion, scale);
      mesh.setMatrixAt(index, matrix);
    });

    mesh.instanceMatrix.needsUpdate = true;
    if (parent) parent.add(mesh);
    return mesh;
  }

  /**
   * Build a `THREE.LOD` for a model that declares `lod` levels in the manifest.
   * Every level must already be loaded (call `await load(...)` first). Returns
   * `null` when any level is missing so no half-built LOD escapes.
   */
  buildLod(name: string, distances?: readonly number[]): THREE.LOD | null {
    const key = resolveModelName(name);
    if (!key) return null;
    const entry = getModelEntry(key);
    if (!entry) return null;

    const levelNames = [key, ...(entry.lod ?? [])];
    if (!levelNames.every((levelName) => this.loaded.has(levelName))) return null;

    const dists = distances ?? DEFAULT_LOD_DISTANCES;
    const lod = new THREE.LOD();
    levelNames.forEach((levelName, index) => {
      const handle = this.create(levelName);
      if (handle) lod.addLevel(handle.scene, dists[index] ?? 0);
    });
    lod.autoUpdate = true;
    return lod;
  }

  /**
   * Expand a canonical model name into itself plus every `lod` level it
   * declares (recursively, so nested chains would also be covered). Unknown
   * names yield an empty list; a `lod` name missing from the manifest is kept
   * so `fetch` records the failure instead of silently dropping the level.
   */
  private lodChain(key: string): string[] {
    const chain: string[] = [];
    const seen = new Set<string>();
    const visit = (name: string): void => {
      if (seen.has(name)) return;
      seen.add(name);
      chain.push(name);
      const entry = getModelEntry(name);
      for (const lodName of entry?.lod ?? []) visit(lodName);
    };
    visit(key);
    return chain;
  }

  /** Fetch + cache one model by canonical name; resolves null on any failure. */
  private fetch(key: string): Promise<GLTF | null> {
    const existing = this.cache.get(key);
    if (existing) return existing;

    const entry = getModelEntry(key);
    if (!entry) {
      this.failures.set(key, 'missing manifest entry');
      return Promise.resolve(null);
    }

    const url = this.baseUrl + entry.file;
    const promise = this.loader
      .loadAsync(url)
      .then((gltf) => {
        this.loaded.set(key, gltf);
        this.failures.delete(key);
        return gltf;
      })
      .catch((error: unknown) => {
        this.failures.set(key, error instanceof Error ? error.message : String(error));
        return null;
      });
    this.cache.set(key, promise);
    return promise;
  }

  /** Clone a loaded scene; skinned models get their own skeleton. */
  private cloneScene(gltf: GLTF, skinned: boolean): THREE.Group {
    if (skinned) {
      return SkeletonUtils.clone(gltf.scene) as THREE.Group;
    }
    // Shares geometry + materials across instances (cheap per-instance clone).
    return gltf.scene.clone(true);
  }

  /** Collect a single instancable geometry+material from a loaded GLTF. */
  private extractInstancedSource(gltf: GLTF): {
    geometry: THREE.BufferGeometry;
    material: THREE.Material;
  } | null {
    const meshes: THREE.Mesh[] = [];
    gltf.scene.traverse((object) => {
      if ((object as THREE.Mesh).isMesh) meshes.push(object as THREE.Mesh);
    });
    if (meshes.length === 0) return null;

    const material = firstMaterial(meshes[0]);
    if (!material) return null;

    if (meshes.length === 1) {
      return { geometry: meshes[0].geometry, material };
    }

    // Merge multi-mesh props into one instancable geometry (bake world
    // transforms so the merged result matches the source scene).
    const geometries = meshes.map((mesh) => {
      mesh.updateWorldMatrix(true, false);
      return mesh.geometry.clone().applyMatrix4(mesh.matrixWorld);
    });
    const merged = mergeGeometries(geometries, false);
    if (!merged) {
      geometries.forEach((geometry) => geometry.dispose());
      return null;
    }
    return { geometry: merged, material };
  }
}
